/**
 * Assets → Drivers: what each driver is qualified for.
 *
 * - Largest vehicle (T): the heaviest weight class they may drive, chosen from
 *   the classes on the vehicle roster (Payload (T)); they may drive that class
 *   and anything lighter.
 * - ADR certified (yes / no) and the certificate's expiry date.
 *
 * Values are entered here (Asserted, DriverAttributesDeclared). Largest vehicle
 * and ADR can also carry a suggestion from job history
 * (DriverCapabilitiesInferred, provenance Inferred), shown only in an empty
 * cell and never saved unless a person fills it in and saves.
 */

import { formatYesNo, type CapabilitySuggestion } from './assetCapabilities';
import { parseTypicalTonnes } from './assetPayloadTonnes';
import { ukTodayYmd } from './ukTime';

export const DRIVER_SUGGESTABLE_FIELDS = ['largest_vehicle_t', 'adr_certified'] as const;
export type DriverSuggestableField = (typeof DRIVER_SUGGESTABLE_FIELDS)[number];
export type DriverEditableField = DriverSuggestableField | 'adr_expiry';

export interface DriverQualifications {
  largest_vehicle_t: string;
  adr_certified: string;
  adr_expiry: string;
}

export interface DriverInferred {
  source_driver_name?: string;
  matched_by?: 'identifier' | 'name_words';
  suggestions?: Partial<Record<DriverSuggestableField, CapabilitySuggestion>>;
}

export interface DriverRosterApiRow {
  entityId: string;
  name: string;
  status?: string;
  group?: string;
  declared?: Partial<DriverQualifications> & { driver_name?: string; asserted_by?: string };
  inferred?: DriverInferred;
}

export interface UnmatchedDriverNameApi {
  source_driver_name: string;
  reason: 'no_match' | 'several';
  candidates: Array<{ entityId: string; name: string }>;
  suggestions: Partial<Record<DriverSuggestableField, CapabilitySuggestion>>;
  counts: { work_days: number };
}

/** A weight class as stored ('44', '7.5'), or '' when not set. */
export function formatWeightClass(value: unknown): string {
  const v = parseTypicalTonnes(String(value ?? ''));
  return v && Number(v) > 0 ? String(Number(v)) : '';
}

/** A YYYY-MM-DD calendar date, or ''. */
export function formatCalendarDate(value: unknown): string {
  const s = String(value ?? '').trim();
  return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : '';
}

export function qualificationsFromRow(row: DriverRosterApiRow): DriverQualifications {
  return {
    largest_vehicle_t: formatWeightClass(row.declared?.largest_vehicle_t),
    adr_certified: formatYesNo(row.declared?.adr_certified),
    adr_expiry: formatCalendarDate(row.declared?.adr_expiry),
  };
}

function normalise(field: DriverSuggestableField, value: unknown): string {
  return field === 'largest_vehicle_t' ? formatWeightClass(value) : formatYesNo(value);
}

export function driverSuggestionFor(
  inferred: DriverInferred | undefined,
  field: DriverSuggestableField,
): CapabilitySuggestion | undefined {
  const s = inferred?.suggestions?.[field];
  if (!s || typeof s.evidence !== 'string') return undefined;
  const value = normalise(field, s.value);
  return { confidence: s.confidence, evidence: s.evidence, ...(value ? { value } : {}) };
}

/** Weight-class choices: the roster's classes plus any value a driver already has, lightest first. */
export function weightClassOptions(rosterClasses: readonly string[], current: readonly string[] = []): string[] {
  const all = new Set<string>();
  for (const v of [...rosterClasses, ...current]) {
    const c = formatWeightClass(v);
    if (c) all.add(c);
  }
  return [...all].sort((a, b) => Number(a) - Number(b));
}

/** 'expired' / 'soon' (within 60 days) / '' for an ADR certificate expiry date. */
export function adrExpiryState(expiry: string, today: string = ukTodayYmd()): 'expired' | 'soon' | '' {
  const d = formatCalendarDate(expiry);
  if (!d) return '';
  if (d < today) return 'expired';
  const [y, m, day] = today.split('-').map(Number);
  const limit = new Date(Date.UTC(y, m - 1, day + 60)).toISOString().slice(0, 10);
  return d <= limit ? 'soon' : '';
}

/**
 * Copy suggested values into empty cells of the given drivers. Values someone
 * entered are never changed. Returns the new values and how many cells were filled.
 */
export function fillDriverSuggestions(
  values: Map<string, DriverQualifications>,
  rows: readonly DriverRosterApiRow[],
  entityIds: Iterable<string>,
): { values: Map<string, DriverQualifications>; filled: number } {
  const byId = new Map(rows.map((r) => [r.entityId, r]));
  const next = new Map(values);
  let filled = 0;
  for (const id of entityIds) {
    const current = next.get(id);
    const row = byId.get(id);
    if (!current || !row) continue;
    let updated: DriverQualifications | null = null;
    for (const f of DRIVER_SUGGESTABLE_FIELDS) {
      if (current[f]) continue;
      const v = driverSuggestionFor(row.inferred, f)?.value;
      if (!v) continue;
      updated = { ...(updated ?? current), [f]: v };
      filled++;
    }
    if (updated) next.set(id, updated);
  }
  return { values: next, filled };
}

/** Changed fields per driver, for the save request. */
export function driverChanges(
  baseline: Map<string, DriverQualifications>,
  current: Map<string, DriverQualifications>,
): Array<{ entityId: string; changed: DriverEditableField[]; values: DriverQualifications }> {
  const out: Array<{ entityId: string; changed: DriverEditableField[]; values: DriverQualifications }> = [];
  for (const [id, values] of current) {
    const before = baseline.get(id);
    const changed = (['largest_vehicle_t', 'adr_certified', 'adr_expiry'] as const).filter((f) => (before?.[f] ?? '') !== values[f]);
    if (changed.length) out.push({ entityId: id, changed, values });
  }
  return out;
}
